import {lazy, useEffect, type JSX} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation, Navigate, Route, Routes} from 'react-router'
import useTypedSelector from '../hooks/use-typed-selector'
import {SystemReinitialising} from '../pages/system-reinitialising/system-reinitialising'
import {
    hasErrorSelector,
    logInStatusSelector,
    userLogInFlowSelector,
} from '../store/state/session-data/selectors'
import {loggedOut, loggingIn} from '../store/state/session-data/action-creators'
import {hasAuthTokenSelector} from '../store/state/auth/selectors'
import {LoginStatus} from '../store/state/session-data/state'
import {GoogleOAuthProvider} from '@react-oauth/google'
import {oauthClientIdsSelector} from '../store/state/config/selectors'

import LoginPage from '../pages/login-v2/login-page'
import {WrapSuspense} from './wrap-suspense'

const LazyLoginPage = lazy(() => import('../pages/login-v2/login-page'))

export function UnauthenticatedRoutes(): JSX.Element | null {
    const dispatch = useDispatch()
    const location = useLocation()

    const loginStatus = useTypedSelector(logInStatusSelector)
    const hasError = useTypedSelector(hasErrorSelector)
    const hasAuthToken = useTypedSelector(hasAuthTokenSelector)
    const loginFlow = useTypedSelector(userLogInFlowSelector)
    const oauthClientIds = useTypedSelector(oauthClientIdsSelector)

    const rebuildingRedux = !loginFlow && loginStatus !== LoginStatus.LOGGING_OUT && !hasError

    useEffect(() => {
        if (loginStatus === LoginStatus.UNKNOWN) {
            if (hasAuthToken) {
                dispatch(loggingIn())
            } else {
                dispatch(loggedOut())
            }
        }
    }, [dispatch, hasAuthToken, loginStatus])

    if (loginStatus === LoginStatus.UNKNOWN) {
        return null
    }

    if (rebuildingRedux) {
        return <SystemReinitialising />
    }

    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <WrapSuspense>
                        <LazyLoginPage admin={false} />
                    </WrapSuspense>
                }
            />
            <Route
                path="/adminLogin"
                element={
                    <WrapSuspense>
                        <GoogleOAuthProvider clientId={oauthClientIds?.google ?? ''}>
                            <LoginPage admin={true} />
                        </GoogleOAuthProvider>
                    </WrapSuspense>
                }
            />
            <Route path="*" element={<Navigate to="/login" state={{from: location}} replace />} />
        </Routes>
    )
}
